/**
 * SHOPIFY FAVORITES SYSTEM - Fixed Version 4
 * - ALL favorites (single or multi-variant) render the REAL <product-form> markup, so your
 *   existing global product-form.js handles the add + opens cart-notification exactly like
 *   everywhere else on the site. No custom add-to-cart JS needed for that path.
 * - Multi-variant products add the first available variant (no "Choose options" modal).
 * - "Add All to Cart" is the one case with no native multi-item form, so it still uses a
 *   custom /cart/add.js call, synced to the correct element: <cart-notification>
 * Save as: assets/favorites.js
 */

class FavoritesManager {
  constructor() {
    this.storageKey = 'shopify_favorites';
    this.favorites = this.loadFavorites();

    // Confirmed from your cart-notification.js: this theme uses <cart-notification>,
    // not <cart-drawer>. Kept cart-drawer as a fallback in case a template swaps it in.
    this.cartElementSelector = 'cart-notification, cart-drawer';

    this.init();
  }

  loadFavorites() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading favorites:', e);
      return [];
    }
  }

  saveFavorites() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.favorites));
      window.dispatchEvent(new CustomEvent('favorites-updated', { detail: this.favorites }));
      this.updateCounter();
    } catch (e) {
      console.error('Error saving favorites:', e);
    }
  }

  updateCounter() {
    const countElements = document.querySelectorAll('.favorites-count');
    countElements.forEach(el => {
      el.textContent = this.favorites.length;
    });
  }

  addFavorite(productData) {
    const exists = this.favorites.some(fav => fav.id === productData.id);

    if (!exists) {
      this.favorites.push({
        id: productData.id,
        handle: productData.handle || '',
        title: productData.title,
        url: productData.url,
        image: productData.image,
        price: productData.price,
        addedAt: new Date().toISOString()
      });
      this.saveFavorites();
      return true;
    }
    return false;
  }

  removeFavorite(productId) {
    const initialLength = this.favorites.length;
    this.favorites = this.favorites.filter(fav => fav.id !== productId);

    if (this.favorites.length < initialLength) {
      this.saveFavorites();
      return true;
    }
    return false;
  }

  isFavorite(productId) {
    return this.favorites.some(fav => fav.id === productId);
  }

  getAllFavorites() {
    return this.favorites;
  }

  clearAllFavorites() {
    this.favorites = [];
    this.saveFavorites();
  }

  getCartElement() {
    return document.querySelector(this.cartElementSelector);
  }

  // --- Only used by "Add All to Cart" — there's no native multi-item form to piggyback on ---
  postCartAdd(items) {
    const cartEl = this.getCartElement();
    const sections =
      cartEl && typeof cartEl.getSectionsToRender === 'function'
        ? cartEl.getSectionsToRender().map((s) => s.id)
        : ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname
      })
    });
  }

  updateCartUI(response) {
    const cartEl = this.getCartElement();

    if (cartEl && typeof cartEl.renderContents === 'function' && response) {
      // Note: when multiple items are added at once, the response has no top-level `.key`,
      // so cart-notification.js's per-item product-row section will silently no-op — the
      // notification-button and cart-icon-bubble sections still update and the popup still opens.
      cartEl.renderContents(response);
      return;
    }

    console.warn('Favorites: no cart-notification/cart-drawer element found — update cartElementSelector in favorites.js');
  }

  async fetchProductVariant(productHandle) {
    const response = await fetch(`/products/${productHandle}.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.status}`);
    }
    const product = await response.json();
    if (!product || !product.variants || product.variants.length === 0) {
      throw new Error('No variants found');
    }
    return product;
  }

  // Add all favorites to cart in one request — no reload, opens the notification once on success
  addAllToCart() {
    if (this.favorites.length === 0) {
      this.showNotification('No products in favorites', 'warning');
      return;
    }

    this.showNotification(`Adding ${this.favorites.length} items...`, 'default');

    const variantPromises = this.favorites.map((fav) =>
      this.fetchProductVariant(fav.handle)
        .then((product) => {
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          return { id: parseInt(variant.id, 10), quantity: 1 };
        })
        .catch((error) => {
          console.error(`Failed for ${fav.title}:`, error);
          return null;
        })
    );

    Promise.all(variantPromises)
      .then((items) => {
        const validItems = items.filter((item) => item !== null);
        if (validItems.length === 0) {
          this.showNotification('Could not add items to cart', 'error');
          return null;
        }
        return this.postCartAdd(validItems);
      })
      .then((response) => {
        if (!response) return null;
        if (!response.ok) throw new Error('Cart error');
        return response.json();
      })
      .then((data) => {
        if (!data) return;
        this.showNotification(`✓ ${this.favorites.length} items added to cart!`, 'added');
        this.updateCartUI(data);
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        this.showNotification('✗ Error adding to cart', 'error');
      });
  }

  // Share favorites
  shareFavorites() {
    const shareText = this.generateShareText();

    const shareOptions = document.createElement('div');
    shareOptions.className = 'share-modal-overlay';
    shareOptions.innerHTML = `
      <div class="share-modal">
        <div class="share-modal-header">
          <h3>Share Your Favorites</h3>
          <button class="share-modal-close" aria-label="Close">&times;</button>
        </div>
        <div class="share-modal-content">
          <div class="share-option">
            <button class="share-btn share-copy" data-action="copy">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              <span>Copy Link</span>
            </button>
            <input type="text" class="share-link-input" readonly value="${this.generateShareLink()}" />
          </div>

          <div class="share-option">
            <button class="share-btn share-email" data-action="email">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              <span>Email</span>
            </button>
          </div>

          <div class="share-option">
            <button class="share-btn share-text" data-action="text">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>Copy Text</span>
            </button>
          </div>

          <div class="share-option">
            <button class="share-btn share-facebook" data-action="facebook">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
              </svg>
              <span>Facebook</span>
            </button>
          </div>

          <div class="share-option">
            <button class="share-btn share-twitter" data-action="twitter">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 002.856-3.915 10.023 10.023 0 01-2.836.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417a9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"></path>
              </svg>
              <span>Twitter</span>
            </button>
          </div>

          <div class="share-option">
            <button class="share-btn share-whatsapp" data-action="whatsapp">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421-7.403h-.004a9.87 9.87 0 00-9.746 9.798c0 2.429.742 4.768 2.149 6.76L2.971 23.39l7.282-2.414a9.86 9.86 0 004.755 1.226h.006c5.448 0 9.887-4.453 9.887-9.981 0-2.655-.675-5.207-1.963-7.44-1.29-2.253-3.329-4.003-5.866-5.043-2.537-1.041-5.289-1.106-7.844-.257m6.35 13.987l-1.52.761a1.805 1.805 0 01-1.028.3 1.89 1.89 0 01-.522-.079c-.51-.146-1.023-.435-1.525-.922-.6-.59-1.278-1.41-1.906-2.418-.672-1.112-.922-1.846-.99-2.055a.745.745 0 01.028-.565c.16-.435.567-.788.938-.788.23 0 .42.078.532.187.155.158.394.534.626 1.108.185.45.41 1.038.535 1.398.084.231.12.511.04.768-.057.203-.28.436-.505.602z"></path>
              </svg>
              <span>WhatsApp</span>
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(shareOptions);

    shareOptions.querySelectorAll('.share-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        this.handleShare(action, shareText);
        shareOptions.remove();
      });
    });

    shareOptions.querySelector('.share-modal-close').addEventListener('click', () => {
      shareOptions.remove();
    });

    shareOptions.addEventListener('click', (e) => {
      if (e.target === shareOptions) shareOptions.remove();
    });
  }

  handleShare(method, text) {
    const link = this.generateShareLink();
    const subject = 'Check out my favorite products!';

    switch (method) {
      case 'copy':
        this.copyToClipboard(link);
        this.showNotification('Link copied!', 'added');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text + '\n\n' + link)}`;
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, '_blank', 'width=600,height=400');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out my favorites! ${link}`)}`, '_blank', 'width=600,height=400');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my favorites! ${link}`)}`, '_blank');
        break;
      case 'text':
        this.copyToClipboard(text);
        this.showNotification('Text copied!', 'added');
        break;
    }
  }

  generateShareLink() {
    return `${window.location.origin}${window.location.pathname}?favorites=${btoa(JSON.stringify(this.favorites))}`;
  }

  generateShareText() {
    if (this.favorites.length === 0) return 'Check out my favorites!';
    let text = 'My favorite products:\n\n';
    this.favorites.forEach((fav, i) => {
      text += `${i + 1}. ${fav.title} - ${fav.price}\n${fav.url}\n\n`;
    });
    return text;
  }

  copyToClipboard(text) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  init() {
    this.initFavoriteButtons();
    this.initRemoveButtons();
    this.initFavoritesPage();
    this.updateButtonStates();
    this.updateCounter();
  }

  initFavoriteButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.favorite-btn');
      if (!btn) return;

      e.preventDefault();
      const productId = parseInt(btn.dataset.productId);
      const handle = btn.dataset.productHandle;
      const title = btn.dataset.productTitle;
      const url = btn.dataset.productUrl;
      const image = btn.dataset.productImage;
      const price = btn.dataset.productPrice;

      const productData = { id: productId, handle, title, url, image, price };

      if (this.isFavorite(productId)) {
        this.removeFavorite(productId);
        this.updateButtonUI(btn, false);
        this.showNotification('Removed from Favorites', 'removed');
      } else {
        this.addFavorite(productData);
        this.updateButtonUI(btn, true);
        this.showNotification('Added to Favorites', 'added');
      }
    });
  }

  initRemoveButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn-remove-favorite');
      if (!btn) return;

      e.preventDefault();
      const productId = parseInt(btn.dataset.productId);

      const item = document.querySelector(`.product-item[data-product-id="${productId}"]`);
      if (item) {
        item.style.animation = 'fadeOut 0.3s ease-out';
        item.addEventListener('animationend', () => {
          item.remove();
          this.checkEmptyState();
        }, { once: true });
      } else {
        this.checkEmptyState();
      }

      this.removeFavorite(productId);
      this.showNotification('Removed from Favorites', 'removed');
    });
  }

  initFavoritesPage() {
    const container = document.getElementById('favorites-container');
    if (!container) return;

    this.renderFavorites();

    const shareBtn = document.getElementById('share-favorites-btn');
    if (shareBtn) shareBtn.addEventListener('click', () => this.shareFavorites());

    const addAllBtn = document.getElementById('add-all-to-cart-btn');
    if (addAllBtn) addAllBtn.addEventListener('click', () => this.addAllToCart());

    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.favorites = this.loadFavorites();
        this.renderFavorites();
      }
    });
  }

  renderFavorites() {
    const container = document.getElementById('favorites-container');
    const emptyState = document.getElementById('empty-favorites');

    if (!container) return;

    if (this.favorites.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';

    container.className = 'collection-products product-grid-s';
    // Add buttons start as a loading placeholder, then get upgraded to the real
    // add-to-cart markup once we know each product's first available variant.
    container.innerHTML = this.favorites.map(fav => this.createProductItemHTML(fav)).join('');

    this.upgradeAddButtons();
  }

  createProductItemHTML(fav) {
    return `
      <div class="product-item" data-product-id="${fav.id}">
        <div class="product-image-wrapper">
          <a href="${fav.url}" class="product-image">
            <img src="${fav.image}" alt="${this.escapeHtml(fav.title)}" loading="lazy">
          </a>
        </div>
        <div class="product-info">
          <h3 class="product-title">
            <a href="${fav.url}">${this.escapeHtml(fav.title)}</a>
          </h3>
          <div class="product-price">${fav.price}</div>
          <div class="product-actions">
          
            <span class="add-to-cart-slot" data-product-handle="${fav.handle}">
              <button class="button button-small" disabled>...</button>
            </span>
            <button class="button button-small btn-remove-favorite" data-product-id="${fav.id}">
              ✕
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // Replaces the placeholder with the real native <product-form> add-to-cart form for
  // EVERY product (single or multi-variant). It adds the first available variant and is
  // handled entirely by your existing global product-form.js, identical to the collection page.
  async upgradeAddButtons() {
    const slots = document.querySelectorAll('.add-to-cart-slot');

    for (const slot of slots) {
      const handle = slot.dataset.productHandle;
      if (!handle) continue;

      try {
        const product = await this.fetchProductVariant(handle);
        slot.innerHTML = this.buildNativeAddFormMarkup(product);
      } catch (err) {
        console.error(`Add button upgrade failed for ${handle}:`, err);
        slot.innerHTML = '<button class="button button-small" disabled>Unavailable</button>';
      }
    }
  }

  // Matches your real collection-page single-variant markup exactly, so product-form.js
  // (already global on your site) handles the AJAX add and opens cart-notification itself.
  buildNativeAddFormMarkup(product) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const disabled = !variant.available;

    return `
      <product-form data-section-id="favorites">
        <form method="post" action="/cart/add" class="form" novalidate="novalidate" data-type="add-to-cart-form">
          <input type="hidden" name="id" value="${variant.id}" class="product-variant-id" ${disabled ? 'disabled' : ''}>
          <button
            type="submit"
            name="add"
            class="button button-small quick-add__submit"
            aria-live="polite"
            data-sold-out-message="true"
            ${disabled ? 'disabled' : ''}
          >
           
          </button>
        </form>
      </product-form>
    `;
  }

  // NOTE: No longer used. Kept only in case you want the variant-picker modal back
  // (call it from upgradeAddButtons() for products with product.variants.length > 1).
  buildQuickAddModalMarkup(product) {
    return `
      <modal-opener data-modal="#QuickAdd-${product.id}">
        <button
          type="button"
          class="button button-small quick-add__submit"
          aria-haspopup="dialog"
          data-product-url="/products/${product.handle}"
        >
         
        </button>
      </modal-opener>
      <quick-add-modal id="QuickAdd-${product.id}" class="quick-add-modal">
        <div
          role="dialog"
          aria-modal="true"
          class="quick-add-modal__content global-settings-popup"
          tabindex="-1"
        >
          <button type="button" class="quick-add-modal__toggle" aria-label="Close">&times;</button>
          <div id="QuickAddInfo-${product.id}" class="quick-add-modal__content-info"></div>
        </div>
      </quick-add-modal>
    `;
  }

  updateButtonUI(btn, isActive) {
    if (isActive) {
      btn.classList.add('active', 'favorited');
      btn.setAttribute('aria-pressed', 'true');
    } else {
      btn.classList.remove('active', 'favorited');
      btn.setAttribute('aria-pressed', 'false');
    }
  }

  updateButtonStates() {
    document.querySelectorAll('.favorite-btn').forEach(btn => {
      const productId = parseInt(btn.dataset.productId);
      const isFav = this.isFavorite(productId);
      this.updateButtonUI(btn, isFav);
    });
  }

  showNotification(message, type = 'default') {
    const notification = document.createElement('div');
    notification.className = `favorites-notification ${type}`;
    notification.textContent = message;
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');

    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add('show'), 10);
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  checkEmptyState() {
    const container = document.getElementById('favorites-container');
    const emptyState = document.getElementById('empty-favorites');

    if (this.favorites.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.favoritesManager = new FavoritesManager();
  });
} else {
  window.favoritesManager = new FavoritesManager();
}

window.addToFavorites = (productId, title, url, image, price, handle) => {
  if (window.favoritesManager) {
    window.favoritesManager.addFavorite({ id: productId, handle, title, url, image, price });
  }
};

window.removeFromFavorites = (productId) => {
  if (window.favoritesManager) {
    window.favoritesManager.removeFavorite(productId);
  }
};

window.getFavorites = () => {
  return window.favoritesManager ? window.favoritesManager.getAllFavorites() : [];
};

window.addAllFavoritesToCart = () => {
  if (window.favoritesManager) {
    window.favoritesManager.addAllToCart();
  }
};

window.shareFavorites = () => {
  if (window.favoritesManager) {
    window.favoritesManager.shareFavorites();
  }
};